import React from 'react';
import apiRequest, { getApiBaseUrl } from '../../services/apiClient';
import { getCsrfToken } from '../../services/authService';
import logger from '../../services/logger';
import AdminToast from '../../components/AdminToast';
import AdminConfirmModal from '../../components/AdminConfirmModal';
import { resolveMediaUrl } from '../../../siteSettings';

const PAGE_SIZE = 12;
const emptySection = { title: 'Galerie', items: [] };

const normalizeItems = (items) => (Array.isArray(items) ? items : [])
  .map((item) => ({ image: item && typeof item.image === 'string' ? item.image : '' }))
  .filter((item) => item.image);

const AdminGallery = () => {
  const [section, setSection] = React.useState(emptySection);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [loadingProgress, setLoadingProgress] = React.useState(8);
  const [uploading, setUploading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  const [deleteIndex, setDeleteIndex] = React.useState(null);
  const [success, setSuccess] = React.useState('');
  const [error, setError] = React.useState('');
  const fileInputRef = React.useRef(null);

  React.useEffect(() => {
    const timer = window.setInterval(() => setLoadingProgress((value) => Math.min(value + 9, 92)), 160);
    apiRequest('/admin-gallery/get.php')
      .then((response) => {
        const data = response.data || {};
        setSection({ title: 'Galerie', items: normalizeItems(data.items) });
        setLoadingProgress(100);
      })
      .catch((requestError) => { setError(requestError.message); logger.error('Gallery load failed', { requestId: requestError.requestId }); })
      .finally(() => { window.clearInterval(timer); setLoading(false); });
    return () => window.clearInterval(timer);
  }, []);

  React.useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(section.items.length / PAGE_SIZE));
    if (page > totalPages) setPage(totalPages);
  }, [page, section.items.length]);

  const persist = async (items, message) => {
    const nextItems = normalizeItems(items);
    setSection({ title: 'Galerie', items: nextItems });
    setSaving(true); setError(''); setSuccess('');
    try {
      await apiRequest('/admin-gallery/update.php', { method: 'POST', headers: { 'X-CSRF-Token': getCsrfToken() }, body: JSON.stringify({ title: 'Galerie', items: nextItems }) });
      setSuccess(message);
    } catch (requestError) { setError(requestError.message); logger.error('Gallery save failed', { requestId: requestError.requestId }); }
    finally { setSaving(false); }
  };

  const uploadFile = (file, fileIndex, fileCount) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const body = new FormData(); body.append('file', file); body.append('kind', 'gallery');
    xhr.open('POST', `${getApiBaseUrl()}/admin-gallery/upload.php`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-CSRF-Token', getCsrfToken());
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) setUploadProgress(Math.round(((fileIndex + event.loaded / event.total) / fileCount) * 100));
    };
    xhr.onload = () => {
      let response = null;
      try { response = JSON.parse(xhr.responseText || '{}'); } catch (parseError) { reject(new Error('Réponse d’upload invalide.')); return; }
      if (xhr.status >= 200 && xhr.status < 300 && response.success !== false && response.data && response.data.url) resolve(response.data.url);
      else reject(new Error(response.message || 'L’image n’a pas pu être envoyée.'));
    };
    xhr.onerror = () => reject(new Error('L’image n’a pas pu être envoyée.'));
    xhr.send(body);
  });

  const uploadImages = async (event) => {
    const input = event.target;
    const files = Array.prototype.slice.call(input.files || []);
    if (!files.length || uploading) return;
    setUploading(true); setUploadProgress(0); setError(''); setSuccess('');
    try {
      const uploaded = [];
      for (let index = 0; index < files.length; index += 1) {
        const url = await uploadFile(files[index], index, files.length);
        uploaded.push({ image: url });
      }
      await persist(section.items.concat(uploaded), `${uploaded.length} image${uploaded.length > 1 ? 's' : ''} enregistrée${uploaded.length > 1 ? 's' : ''}.`);
      setPage(Math.max(1, Math.ceil((section.items.length + uploaded.length) / PAGE_SIZE)));
    } catch (requestError) { setError(requestError.message); logger.error('Gallery image upload failed', { requestId: requestError.requestId }); }
    finally { setUploadProgress(100); setUploading(false); input.value = ''; }
  };

  const confirmRemoveItem = (index) => {
    persist(section.items.filter((item, itemIndex) => itemIndex !== index), 'Image supprimée.');
  };

  const removeItem = (index) => setDeleteIndex(index);

  const moveItem = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= section.items.length) return;
    const items = [...section.items]; [items[index], items[target]] = [items[target], items[index]];
    persist(items, 'Ordre de la galerie enregistré.');
  };

  if (loading) return <div className="admin-page"><div className="admin-gallery-loading"><span>Chargement de la galerie…</span><div className="admin-progress"><div className="admin-progress-bar" style={{ width: `${loadingProgress}%` }} /></div></div></div>;

  const totalPages = Math.max(1, Math.ceil(section.items.length / PAGE_SIZE));
  const pageStart = (page - 1) * PAGE_SIZE;
  const pageItems = section.items.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div className="admin-page">
      <div className="admin-page-toolbar"><div><span className="admin-eyebrow">GALERIE</span><h1>Galerie</h1><p>Gérez les images affichées dans la galerie publique.</p></div><div className="admin-gallery-toolbar-action"><button type="button" className="admin-button admin-button-primary admin-gallery-add-button" onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={uploading || saving}><i className="fa fa-plus" /> {uploading ? 'Envoi en cours…' : 'Ajouter des images'}</button><input ref={fileInputRef} className="admin-gallery-file-input" type="file" accept="image/*" multiple onChange={uploadImages} /></div></div>
      <AdminToast message={error} type="danger" onClose={() => setError('')} /><AdminToast message={success} type="success" onClose={() => setSuccess('')} />
      {uploading && <div className="admin-gallery-upload-progress"><div className="admin-progress-label"><span>Upload des images…</span><strong>{uploadProgress}%</strong></div><div className="admin-progress"><div className="admin-progress-bar" style={{ width: `${uploadProgress}%` }} /></div></div>}
      <div className="admin-gallery-grid">{pageItems.map((item, index) => { const itemIndex = pageStart + index; return <article className="admin-gallery-card" key={`${item.image}-${itemIndex}`}><a className="admin-image-preview-link admin-gallery-card-image" href={resolveMediaUrl(item.image)} target="_blank" rel="noopener noreferrer"><img src={resolveMediaUrl(item.image)} alt={`Galerie ${itemIndex + 1}`} /></a><div className="admin-gallery-card-footer"><span>Image #{itemIndex + 1}</span><div className="admin-gallery-card-actions"><button type="button" className="admin-section-order" onClick={() => moveItem(itemIndex, -1)} disabled={itemIndex === 0} title="Monter"><i className="fa fa-chevron-up" /></button><button type="button" className="admin-section-order" onClick={() => moveItem(itemIndex, 1)} disabled={itemIndex === section.items.length - 1} title="Descendre"><i className="fa fa-chevron-down" /></button><button type="button" className="admin-section-remove" onClick={() => removeItem(itemIndex)} title="Supprimer" aria-label="Supprimer"><i className="fa fa-trash" /></button></div></div></article>; })}</div>
      {!section.items.length && <div className="admin-gallery-empty">Aucune image. Cliquez sur « Ajouter des images ».</div>}
      <div className="admin-pagination"><span>Page {page} sur {totalPages} · {section.items.length} image{section.items.length > 1 ? 's' : ''}</span><div><button className="admin-button admin-button-light admin-button-small" type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>Précédent</button><button className="admin-button admin-button-light admin-button-small" type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Suivant</button></div></div>
      <AdminConfirmModal isOpen={deleteIndex !== null} title="Supprimer cette image ?" message="Cette image sera retirée de la galerie publique." onCancel={() => setDeleteIndex(null)} onConfirm={() => { const target = deleteIndex; setDeleteIndex(null); if (target !== null) confirmRemoveItem(target); }} />
    </div>
  );
};

export default AdminGallery;
